import { useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Delete } from '@mint-lib/icons';
import {
  Avatar,
  AvatarFallback,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  Divider,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import type {
  NormalizedTemplateCollaborator,
  UserInfo,
} from '../../api/types.js';
import {
  useSetTemplateCollaborators,
  useTemplateCollaborators,
  useUsersInfo,
} from '../../queries/index.js';
import { normalizeString } from '../../utils/index.js';
import { Loader } from '../atoms/Loader.jsx';
import { MultiSelectCombobox } from '../atoms/MultiSelectCombobox.jsx';
import { RichTooltip } from '../atoms/RichTooltip.jsx';

type ExtendedCollaborator = NormalizedTemplateCollaborator & UserInfo;

type TemplateCollaboratorsDialogProps = {
  templateId: string;
  campaignId: number | null;
  mediaPlanId: string | null;
  onSuccess: () => void;
  onClose: () => void;
};

export const TemplateCollaboratorsDialog = ({
  templateId,
  campaignId,
  mediaPlanId,
  onSuccess,
  onClose,
}: TemplateCollaboratorsDialogProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const {
    data: {
      list: templateCollaboratorsList = [],
      byId: templateCollaboratorsById = {},
    },
    isLoading: isLoadingCollaborators,
  } = useTemplateCollaborators(templateId);

  const {
    data: { list: usersList, byId: usersById },
    isLoading: isLoadingUsers,
  } = useUsersInfo({ campaignId, mediaPlanId, includeOwners: true });

  const isLoading = isLoadingCollaborators || isLoadingUsers;

  const persistedCollaborators = useMemo(
    () =>
      templateCollaboratorsList
        .filter(({ id }) => usersById[id])
        .map((templateCollaborator) => ({
          ...templateCollaborator,
          ...usersById[templateCollaborator.id],
        })),
    [templateCollaboratorsList, usersById],
  );

  const [draftCollaborators, setDraftCollaborators] = useState<
    ExtendedCollaborator[] | null
  >(null);
  const selectedCollaborators = draftCollaborators ?? persistedCollaborators;
  const isFormPristine = draftCollaborators === null;
  const isFormValid = Boolean(selectedCollaborators.length);

  const options = useMemo(() => {
    const uniqueUsers = Array.from(
      new Map(usersList.map((user) => [user.id, user])).values(),
    );

    return uniqueUsers.map((user) => ({
      ...user,
      ...templateCollaboratorsById[user.id],
    }));
  }, [templateCollaboratorsById, usersList]);

  const handleCollaboratorsChange = (value: ExtendedCollaborator[]) => {
    setDraftCollaborators(value);
  };

  const { mutateAsync: setTemplateCollaborators, isPending: isSubmitting } =
    useSetTemplateCollaborators(templateId);

  const handleSubmit = async () => {
    const payload = selectedCollaborators.map((selectedCollaborator) => ({
      id: selectedCollaborator.collaboratorId,
      userId: selectedCollaborator.id,
      email: selectedCollaborator.email,
      collaborationLevel:
        selectedCollaborator.collaborationLevel ?? 'collaborator',
    }));
    const hasOwner = payload.some(({ collaborationLevel }) => {
      return collaborationLevel === 'owner';
    });
    if (!hasOwner) {
      payload[0].collaborationLevel = 'owner';
    }
    try {
      await setTemplateCollaborators(payload);
      onSuccess();
      onClose();
    } catch {
      /* empty */
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot className="h-[430px] w-[520px]">
          <DialogHeader>
            <DialogTitle>{t('Add collaborators')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('Choose users to collaborate on this template.')}
            </DialogDescription>
          </DialogHeader>
          <DialogContent>
            {isLoading ? (
              <Loader />
            ) : (
              <CollaboratorsDialogContent
                selectedCollaborators={selectedCollaborators}
                handleCollaboratorsChange={handleCollaboratorsChange}
                options={options}
              />
            )}
          </DialogContent>
          <DialogFooter>
            <Button size="large" variant="text" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              size="large"
              onClick={handleSubmit}
              disabled={isFormPristine || !isFormValid || isSubmitting}
            >
              {t('Save')}
            </Button>
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

const MAX_COLLABORATORS_NUMBER = 3;

const CollaboratorsDialogContent = ({
  selectedCollaborators,
  handleCollaboratorsChange,
  options,
}: {
  selectedCollaborators: ExtendedCollaborator[];
  handleCollaboratorsChange: (value: ExtendedCollaborator[]) => void;
  options: ExtendedCollaborator[];
}) => {
  const { t } = useTranslation('@myn/import-hub');

  const isCollaboratorsLimitReached =
    selectedCollaborators.length === MAX_COLLABORATORS_NUMBER;

  const filterOptions = (
    options: ExtendedCollaborator[],
    searchValue: string,
  ) => {
    if (searchValue.length >= 2) {
      const searchWords = searchValue.split(' ');
      return options.filter((user) => {
        const matchCount = searchWords.filter((word) => {
          const normalizedWord = normalizeString(word);
          return [user.firstName, user.lastName, user.email]
            .map(normalizeString)
            .join(' ')
            .includes(normalizedWord);
        }).length;

        return matchCount === searchWords.length;
      });
    }
    return options;
  };

  const collaboratorLabel = (selectedCollaborator: ExtendedCollaborator) => {
    const { firstName, lastName, email } = selectedCollaborator;
    const fullName = `${firstName} ${lastName}`;
    const emailPart = email ? ` ${email}` : '';

    return `${fullName}${emailPart}`;
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p
        className={typographyVariants({
          variant: 'bodyLong01',
          color: 'secondary',
        })}
      >
        {t(
          'Add collaborators to this template to receive email notifications on auto-import file status updates.',
        )}
      </p>
      <MultiSelectCombobox
        placeholder={t('Search')}
        inputValue={selectedCollaborators
          .map((collaborator) => collaborator.email)
          .join(', ')}
        selectedValues={selectedCollaborators}
        options={options}
        getOptionValue={(option) => String(option.id)}
        getOptionLabel={(option) =>
          `${option.firstName} ${option.lastName} (${option.email})`
        }
        getOptionDisabled={(_, { isSelected }) => {
          return isCollaboratorsLimitReached && !isSelected;
        }}
        renderOption={(option, { isSelected, isDisabled }) => (
          <RichTooltip
            asChild={true}
            description={t(
              "You've reached the maximum number of collaborators",
            )}
            disabled={!isDisabled}
            side="top"
            align="center"
          >
            <div className="flex min-w-0 items-center gap-1">
              <Checkbox
                checked={isSelected}
                disabled={isDisabled}
                className={isDisabled ? 'cursor-default' : 'cursor-pointer'}
              />
              <TruncateText>
                <div>{`${option.firstName} ${option.lastName} (${option.email})`}</div>
              </TruncateText>
            </div>
          </RichTooltip>
        )}
        onChange={handleCollaboratorsChange}
        withSearch={true}
        helperText={t(
          'Max number of collaborators for template: {{MAX_COLLABORATORS_NUMBER}}',
          { MAX_COLLABORATORS_NUMBER },
        )}
        noOptionsText={t(
          `No results found. This user might not have permissions to view the campaign mapped in this template.`,
        )}
        filterOptions={filterOptions}
        className="w-full"
        menuClassname="max-h-[210px]"
        menuItemClassName="grid-cols-[1fr]"
      />
      {Boolean(selectedCollaborators.length) && (
        <>
          <Divider orientation="horizontal" />
          <p
            className={typographyVariants({
              variant: 'caption2',
              color: 'secondary',
            })}
          >
            {t('{{collaboratorsCount}} {{collaborators}} in this template', {
              collaboratorsCount: selectedCollaborators.length,
              collaborators:
                selectedCollaborators.length > 1
                  ? 'collaborators'
                  : 'collaborator',
            })}
          </p>
        </>
      )}
      <ul>
        {selectedCollaborators.map((selectedCollaborator) => (
          <li
            key={selectedCollaborator.id}
            className="flex items-center gap-2 px-0 py-1"
          >
            <Avatar size="small">
              <AvatarFallback
                firstName={selectedCollaborator.firstName}
                lastName={selectedCollaborator.lastName}
              />
            </Avatar>
            <TruncateText>
              <div
                className={cn(
                  typographyVariants({ variant: 'bodyShort01' }),
                  'min-w-0',
                )}
              >
                {collaboratorLabel(selectedCollaborator)}
              </div>
            </TruncateText>
            <span
              className={cn(
                typographyVariants({
                  variant: 'bodyShort01',
                  color: 'secondary',
                }),
                'ml-auto capitalize',
              )}
            >
              {selectedCollaborator.collaborationLevel || 'collaborator'}
            </span>
            <Button
              onClick={() =>
                handleCollaboratorsChange(
                  selectedCollaborators.filter(
                    ({ id }) => id !== selectedCollaborator.id,
                  ),
                )
              }
              aria-label={t('Delete template collaborator')}
              data-dd-action-name="Delete template collaborator"
              icon
              variant="text"
              size="small"
              color="error"
              className="fill-text-error"
            >
              <Delete />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};
